import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { useUserFilters } from '../../../hooks/useUserFilters';
import { useErrorMessages, useUserSession } from '../../../models/hooks/appStateHooks';
import { EventLogger, useEventLogging } from '../../../models/Logging';
import { getDisplayName } from '../../../models/NameUtils';
import {
	invalidateAccountLoad,
	invalidateAccountUserCount,
	invalidateInfiniteUsers,
	useAccountLoad,
	useAccountUserCount,
	useDeactivateUserMutation,
	useDeleteUserMutation,
	useReactivateUserMutation,
	useUpdateUserCardSettingsMutation,
	useUpdateUserEmailMutation,
	useUpdateUserRoleMutation,
} from '../../../queries';
import { useLoadAllUsers } from '../../../queries/User/useLoadAllUsers';
import { ITemplate } from '../../../viewmodels/AppViewModels';
import { Dialog, DialogDescription, DialogHeader, DialogPopup, DialogTitle } from '../../components-v2/Dialog';
import { AppBarNavigationItemPortalDestinationId } from '../../components/AppBar';
import { DeactivateUserModal } from '../../components/DeactivateUserModal';
import { DeprecatedCloseButton } from '../../components/DeprecatedCloseButton';
import { EditEmailSignatureModal } from '../../components/email/EditEmailSignature';
import { DangerMessage } from '../../components/errorMessages/DangerMessage';
import { FabContext } from '../../components/FabContext';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { Modal } from '../../components/Modal';
import { NavigationBreadcrumbsBar } from '../../components/NavigationBreadcrumbsBar';
import { Portal } from '../../components/Portal';
import { ScrollView } from '../../components/ScrollView';
import { ISelectBoxOption } from '../../components/SelectBox';
import { InviteEmployeesModal } from '../../components/settings/InviteEmployees';
import { HandwrittenCardSettings } from '../../components/settings/PersonalSettings/HandwrittenCardSettings';
import { UserFilters } from '../../components/UserFilters';
import { INavigationItemProps } from '../MainContainer';
import {
	ManageUsersEditEmail,
	ManageUsersEditMeetingLinks,
	ManageUsersEditSignature,
	ManageUsersFormattedUser,
	ManageUsersListViewHeader,
} from './presentation';
import { styleSheet } from './styles';

interface IProps extends RouteComponentProps<any>, INavigationItemProps {}

interface IState {
	editingEmailSignature?: ITemplate;
	editingUserEmail?: Api.IUser;
	forUserCardSettings?: Api.IUser;
	forUserMeetingLinks?: Api.IUser;
	forUserSignature?: Api.IUser;
	selectedEmailSignature?: ITemplate;
	showInviteEmployees?: boolean;
	sortAscending?: boolean;
	sortColumn?: string;
	userIdToDeactivate?: string;
	userLoading?: Api.IUser;
	users?: Api.IUser[];
	userToAddSignatureFor?: Api.IUser;
}

const _ManageUsers = ({ routeContainerClassName }: IProps) => {
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const { logApiError: _logApiError } = useEventLogging('ManageUsers');
	const [state, setState] = React.useState<IState>({
		editingEmailSignature: null,
		editingUserEmail: null,
		forUserCardSettings: null,
		forUserMeetingLinks: null,
		forUserSignature: null,
		selectedEmailSignature: {},
		showInviteEmployees: false,
		sortAscending: true,
		sortColumn: 'firstName',
		userIdToDeactivate: null,
		userLoading: null,
		userToAddSignatureFor: null,
	});

	const [isUserCardSettingsModalOpen, setIsUserCardSettingsModalOpen] = React.useState(false);
	const [isUserMeetingLinksModalOpen, setIsUserMeetingLinksModalOpen] = React.useState(false);
	const [isUserSignatureModalOpen, setIsUserSignatureModalOpen] = React.useState(false);
	const [hasAutoLoaded, setHasAutoLoaded] = React.useState(false);

	// Direct user loading using proper hook architecture
	const directUserLoader = useLoadAllUsers({
		pageSize: 500,
		onError: error => displayErrorMessage(error),
	});

	const isLoadingAllUsers = directUserLoader.isLoading;
	const loadingError = directUserLoader.error;

	// All users can see all role types in the filter (for viewing purposes)
	// But only super admins can actually change users to/from super admin role
	const userFilters = useUserFilters<Api.IUser>({
		roleOptions: ['superAdmin', 'admin', 'user', 'limitedUser'] as const,
		roleLabels: {
			superAdmin: 'Super Admin',
			admin: 'Administrator',
			user: 'User',
			limitedUser: 'Limited User',
		},
		getUserRole: user => user.role || 'user', // Use the role field directly, default to 'user'
		getDisplayName: user => getDisplayName(user),
	});

	const accountId = React.useMemo(() => userSession.user.accountId, [userSession.user.accountId]);

	const countData = useAccountUserCount({
		accountId,
		meta: {
			toastErrorMessage: 'Could not get the number of users on this account',
		},
	});

	const accountData = useAccountLoad({
		accountId,
		meta: {
			toastErrorMessage: 'Could not load the account information',
		},
	});

	const userDeactivate = useDeactivateUserMutation({
		onError: (result: Api.IOperationResultNoValue) => {
			errorMessage(result.systemMessage, 'Could not deactivate this user');
		},

		onSuccess: () => invalidateUsers({ userIdToDeactivate: null }),
	});

	const userReactivate = useReactivateUserMutation({
		onError: (result: Api.IOperationResultNoValue) => {
			errorMessage(result.systemMessage, 'Could not reactivate this user');
		},
		onSuccess: () => invalidateUsers(),
	});

	const userDelete = useDeleteUserMutation({
		onError: (result: Api.IOperationResultNoValue) => {
			errorMessage(result.systemMessage, 'Could not delete this user');
		},
		onSuccess: () => invalidateUsers(),
	});

	const userRoleUpdate = useUpdateUserRoleMutation({
		onError: () => displayErrorMessage('Unable to edit the role for this user'),
		onSuccess: updatedUser => {
			// Update the single user in the cache immediately
			directUserLoader.updateUser(updatedUser);
			// Also invalidate the infinite query cache
			invalidateInfiniteUsers();
			setState({ ...state, userLoading: null });
		},
	});

	const userSettings = useUpdateUserCardSettingsMutation({
		onError: () => {
			displayErrorMessage('Could not edit user settings');

			setState({ ...state, forUserCardSettings: null, userLoading: null });
			setIsUserCardSettingsModalOpen(false);
		},

		onSuccess: () => {
			setIsUserCardSettingsModalOpen(false);
			invalidateUsers({ forUserCardSettings: null });
			// Refresh user data after successful update
			loadAllUsers();
		},
	});

	const changeUserEmailMutation = useUpdateUserEmailMutation({
		onError: () => {
			displayErrorMessage('Could not edit users email');
			setState({ ...state, editingUserEmail: null });
		},
		onSuccess: () => {
			setState({ ...state, editingUserEmail: null });
			invalidateInfiniteUsers();
		},
	});

	const displayErrorMessage = React.useCallback(
		(errorMsg: string) => {
			if (errorMessages) {
				errorMessages.push({
					messages: [errorMsg],
				});
			}
		},
		[errorMessages]
	);

	const loadAllUsers = directUserLoader.loadAllUsers;

	React.useEffect(() => {
		if (!hasAutoLoaded) {
			setHasAutoLoaded(true);
			loadAllUsers();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hasAutoLoaded]);

	// Get all users from direct API calls
	const allUsers = directUserLoader.users;

	// Filter users based on current filters (no hiding of super admins - they should be visible but not editable)
	const filteredUsers = React.useMemo(() => {
		return userFilters.filterUsers(allUsers);
	}, [allUsers, userFilters]);

	const inviteClick = () => {
		setState({ ...state, showInviteEmployees: true });
	};

	const onInviteEmployeesClose = () => {
		invalidateUsers({ showInviteEmployees: false });
	};

	const onCloseDeactivateUser = (deactivate?: boolean) => {
		if (deactivate) {
			// deactivateUser(state.userIdToDeactivate);

			userDeactivate.mutate({ userId: state.userIdToDeactivate });
		}

		setState({ ...state, userIdToDeactivate: null, userLoading: null });
	};

	const onUserRoleChanged = (option: ISelectBoxOption<string>, user: Api.IUser) => {
		// Check to make sure the value has changed and that this is not a cancel operation
		if (!!option && !!option.value) {
			const newRole = option.value;
			const currentRole = user.role || 'user'; // Default to 'user' if no role is set
			const valueChanged = newRole !== currentRole;

			if (valueChanged) {
				// Call the new web service endpoint to update the user role
				userRoleUpdate.mutate({ role: newRole, userId: user.id });
				setState({ ...state, userLoading: user });
			}
		}
	};

	const deleteClick = (user: Api.IUser) => {
		const userId = user.id;

		userDelete.mutate({ userId });
		setState({ ...state, userLoading: user });
	};

	const deactivateClick = (user: Api.IUser) => {
		setState({ ...state, userIdToDeactivate: user.id, userLoading: user });
	};

	const reactivateClick = (user: Api.IUser) => {
		const userId = user.id;

		userReactivate.mutate({ userId });
		setState({ ...state, userLoading: user });
	};

	const editUserCardSettingsOption = (user: Api.IUser) => {
		setState({ ...state, forUserCardSettings: user, userLoading: user });
		setIsUserCardSettingsModalOpen(true);
	};

	const editMeetingLinksOption = (user: Api.IUser) => {
		setState({ ...state, forUserMeetingLinks: user, userLoading: user });
		setIsUserMeetingLinksModalOpen(true);
	};

	const editUserSignatureOption = (user: Api.IUser) => {
		setState({ ...state, forUserSignature: user, userLoading: user });
		setIsUserSignatureModalOpen(true);
	};

	const handleUserCardSettingsModalRequestClose = () => {
		setState({ ...state, forUserCardSettings: null, userLoading: null });
		setIsUserCardSettingsModalOpen(false);
	};

	const handleUserMeetingLinksModalRequestClose = () => {
		setIsUserMeetingLinksModalOpen(false);

		setState({ ...state, forUserMeetingLinks: null, userLoading: null });
	};

	const handleUserSignatureModalRequestClose = () => {
		setState({ ...state, forUserSignature: null, userLoading: null });
		setIsUserSignatureModalOpen(false);
	};

	const handleEditUserEmail = (newEmail?: string, reason?: string) => {
		changeUserEmailMutation.mutate({
			newEmail,
			reason,
			forUserId: state.editingUserEmail.id,
		});
	};

	const handleEditCardSettingsModalOpenChange = (open: boolean) => {
		if (!open) {
			handleUserCardSettingsModalRequestClose();
		}
	};
	const handleEditCardSettingsSave = (result?: Api.IUserPreferences) => {
		handleUserCardSettingsModalRequestClose();
		userSettings.mutate({ forUser: state.forUserCardSettings.id, settings: result });
	};

	function errorMessage(_: string, message: string) {
		displayErrorMessage(message);
	}

	const editUserEmailClick = (user: Api.IUser) => {
		setState({
			...state,
			editingUserEmail: user,
		});
	};

	const editEmailSignature = (emailSignature: ITemplate, forUser: Api.IUser) => {
		const nextState: IState = {
			editingEmailSignature: emailSignature || {},
		};
		setState({
			...state,
			editingEmailSignature: emailSignature || {},
			userLoading: forUser,
			userToAddSignatureFor: forUser,
		});

		const create = !nextState.editingEmailSignature.id;
		EventLogger.logInput('SignatureButton', `${create ? 'Create' : 'Edit'}Signature`, 'Click', {
			id: nextState.editingEmailSignature.id,
		});
	};

	const onEditEmailSignatureRequestClose = (editedEmailTemplate?: ITemplate, cancel?: boolean) => {
		const nextState: IState = {
			editingEmailSignature: null,

			userToAddSignatureFor: null,
		};

		if (!!editedEmailTemplate && !cancel) {
			onEmailSignatureSelected(null);

			invalidateUsers({ ...nextState, selectedEmailSignature: null });
			// update consumers
			return;
		}

		setState({ ...state, ...nextState, userLoading: null });
	};

	function onEmailSignatureSelected(emailSignature?: ITemplate) {
		const nextState: IState = {
			selectedEmailSignature: emailSignature,
		};

		setState({
			...state,
			...nextState,
		});

		EventLogger.logInput('SignatureButton', `Signature${emailSignature ? 'Selected' : 'Cleared'}`, 'Click', {
			id: emailSignature ? emailSignature.id : null,
		});
	}

	function invalidateUsers(nextState: IState = {}) {
		invalidateInfiniteUsers();
		invalidateAccountLoad();

		invalidateAccountUserCount(userSession.user.accountId);

		setState({ ...state, userLoading: null, ...nextState });

		// Refresh user data to ensure UI shows latest changes
		loadAllUsers();
	}

	return (
		<div className='manageUsers-container'>
			<Portal destination={AppBarNavigationItemPortalDestinationId}>
				<NavigationBreadcrumbsBar currentLocationName='Manage Users' />
			</Portal>
			{isLoadingAllUsers ? (
				<div className={css(styleSheet.loadingContainer)}>
					<LoadingSpinner type='large' />
					<div className={css(styleSheet.loadingMessage)}>Loading all users...</div>
				</div>
			) : accountData.isPending ? (
				<LoadingSpinner type='large' />
			) : null}
			{allUsers.length > 0 || isLoadingAllUsers ? (
				<ScrollView
					className={css(styleSheet.manageUsersContainerListView)}
					contentClassName={routeContainerClassName || undefined}
					hasFixedHeader={true}
					header={
						<>
							<ManageUsersListViewHeader
								loading={
									countData.isPending ||
									accountData.isPending ||
									isLoadingAllUsers ||
									userReactivate.isPending ||
									userDeactivate.isPending ||
									userDelete.isPending ||
									userRoleUpdate.isPending
								}
								consumedUsers={countData.data}
								totalLicenses={accountData?.data?.planDetails?.users}
								inviteClick={inviteClick}
							/>
							{loadingError && (
								<div className={css(styleSheet.errorContainer)}>
									<DangerMessage>
										{loadingError}
										<button
											onClick={() => {
												loadAllUsers();
											}}
											style={{ marginLeft: 10 }}
										>
											Retry
										</button>
									</DangerMessage>
								</div>
							)}
							<UserFilters
								{...userFilters}
								roleOptions={['superAdmin', 'admin', 'user', 'limitedUser'] as const}
								roleLabels={{
									superAdmin: 'Super Admin',
									admin: 'Administrator',
									user: 'User',
									limitedUser: 'Limited User',
								}}
								userCount={filteredUsers.length}
							/>
						</>
					}
				>
					<div className={css(styleSheet.managedUsersEmployeeList)}>
						{filteredUsers.length === 0 ? (
							<p className={css(styleSheet.noneFound)} key='no-results'>
								No results
							</p>
						) : (
							filteredUsers.map(user => (
								<ManageUsersFormattedUser
									key={user?.id}
									deactivateClick={() => deactivateClick(user)}
									deleteClick={() => deleteClick(user)}
									isInUpdatingList={state?.userLoading?.id === user?.id}
									onEditCardSettings={() => editUserCardSettingsOption(user)}
									onEditEmailSignature={() => editEmailSignature(null, user)}
									onEditMeetingLinks={() => editMeetingLinksOption(user)}
									onEditSignatureClick={() => editUserSignatureOption(user)}
									onEditUserEmailClick={() => editUserEmailClick(user)}
									onUserRoleChanged={(role: ISelectBoxOption<string>) => onUserRoleChanged(role, user)}
									reactivateClick={() => reactivateClick(user)}
									user={user}
									userSession={userSession}
								/>
							))
						)}
						{isLoadingAllUsers ? <LoadingSpinner type='large' /> : null}
					</div>
				</ScrollView>
			) : null}
			<InviteEmployeesModal
				account={accountData.data}
				modalProps={{
					isOpen: state.showInviteEmployees,
					onRequestClose: onInviteEmployeesClose,
				}}
				userSession={userSession}
			/>
			{Boolean(state.userIdToDeactivate) && (
				<DeactivateUserModal userId={state.userIdToDeactivate} onClose={onCloseDeactivateUser} />
			)}
			{state.userToAddSignatureFor ? (
				<EditEmailSignatureModal
					emailSignature={state.editingEmailSignature}
					forUser={state.userToAddSignatureFor}
					modalProps={{
						isOpen: Boolean(state.editingEmailSignature),
						onRequestClose: onEditEmailSignatureRequestClose,
					}}
				/>
			) : null}
			<Dialog open={isUserCardSettingsModalOpen} onOpenChange={handleEditCardSettingsModalOpenChange}>
				<DialogPopup>
					<DialogHeader>
						<DialogTitle>Cards Setting</DialogTitle>
						<DialogDescription>
							Setup your card with personalization writing style, signature and logo.
						</DialogDescription>
					</DialogHeader>
					<HandwrittenCardSettings
						forUser={state.forUserCardSettings}
						onSave={handleEditCardSettingsSave}
						onCancel={() => handleEditCardSettingsModalOpenChange(false)}
					/>
				</DialogPopup>
			</Dialog>
			<ManageUsersEditSignature
				forUser={state.forUserSignature}
				modalProps={{
					headerAccessory: (
						<div className={css(styleSheet.manageUsersModalHeader)}>
							<div className={css(styleSheet.manageUsersModalTitle)}>
								Manage {getDisplayName(state.forUserSignature)}&lsquo;s Signature
							</div>
							<DeprecatedCloseButton onClick={() => handleUserSignatureModalRequestClose()} />
						</div>
					),
					isOpen: isUserSignatureModalOpen,
					onRequestClose: handleUserSignatureModalRequestClose,
				}}
			/>

			<Dialog open={!!state.editingUserEmail} onOpenChange={() => setState({ ...state, editingUserEmail: null })}>
				<DialogPopup>
					<DialogHeader>
						<DialogTitle>Change User Email</DialogTitle>
					</DialogHeader>
					<ManageUsersEditEmail onSave={handleEditUserEmail} />
				</DialogPopup>
			</Dialog>

			{isUserMeetingLinksModalOpen ? (
				<Modal
					isOpen={true}
					headerAccessory={
						<div className={css(styleSheet.manageUsersModalHeader)}>
							<div className={css(styleSheet.manageUsersModalTitle)}>
								Manage {getDisplayName(state.forUserMeetingLinks)}&lsquo;s Meeting Links
							</div>
							<DeprecatedCloseButton onClick={() => handleUserMeetingLinksModalRequestClose()} />
						</div>
					}
					className={css(styleSheet.manageUsersModal)}
					onRequestClose={handleUserMeetingLinksModalRequestClose}
				>
					<ManageUsersEditMeetingLinks
						forUser={state.forUserMeetingLinks}
						onEditMeetingClicked={handleUserMeetingLinksModalRequestClose}
					/>
				</Modal>
			) : null}
			<FabContext appearance={{ hidden: true }} />
		</div>
	);
};

const ObservableManageUsers = observer(_ManageUsers);
export const ManageUsers = withRouter(ObservableManageUsers);
